import { Injectable } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';

import {
  getEvent,
  EventWithDocId,
  getActivePage,
  getTickets,
} from '@yoimo/client-sdk/events';
import {
  getVideo,
  getVideosFromSource,
  VideoSourceQueryOption,
  VideoWithDocId,
} from '@yoimo/client-sdk/videos';
import {
  LiveAndUpcomingListModule,
  Page,
  TagsListModule,
  TicketPrice,
  VideoListSource,
} from '@yoimo/interfaces';
import { BrandingService } from '@yoimo/joymo-ui';

import {
  combineLatest,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';

import { VideoPageScope } from '@core/interfaces/types';
import { MetaService, ScopeService, TicketsService } from '@core/services';
import { environment } from '@environment';

import {
  getVideoFilterPredicate,
  startsAfter,
  startsBefore,
} from '../../features/video/video.utils';

@Injectable({ providedIn: 'root' })
export class VideoPageResolver implements Resolve<VideoPageScope> {
  constructor(
    private fs: Firestore,
    private metaService: MetaService,
    private scopeService: ScopeService,
    private brandingService: BrandingService
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<VideoPageScope> {
    const videoId = route.paramMap.get('videoId');

    if (!videoId) throw new Error('VideoIdNotFound');

    const video$ = this.scopeService.scope$.pipe(
      switchMap((scope) => getVideo(this.fs, videoId, scope.scopeId)),
      tap((video) => {
        this.metaService.setTags({
          title: video.name,
          description: video.description,
          image: video.posterImage,
        });
      }),
      shareReplay(1)
    );
    const event$ = video$.pipe(
      switchMap((video) => {
        if (!video.eventId) return of(undefined);
        return getEvent(this.fs, video.channelId, video.eventId);
      }),
      tap((event) => event && this.applyBranding(event))
    );
    return combineLatest([
      this.scopeService.listenToChannel(),
      video$,
      event$,
    ]).pipe(
      map(([channel, video, event]) => {
        const { channelsWithVideoFeedback } = environment.behavior.testFeatures;

        return {
          canProvideFeedback: channelsWithVideoFeedback.includes(channel.docId),
          channel,
          cheapestAccess: this.getCheapestPrice(video, event),
          event,
          filterPredicate: getVideoFilterPredicate(video.docId),
          homepageUrl: this.scopeService.getChannelUrlFromChannel(channel),
          liveAndUpcoming: event
            ? this.getLiveAndUpcoming(event.docId)
            : undefined,
          prevAndNext$: event ? this.getPrevAndNext$(video, event) : undefined,
          tagsModule: this.getTagsModule(video),
          video,
        };
      })
    );
  }

  private applyBranding(event: EventWithDocId): void {
    const activePage: Page | undefined = getActivePage(event);
    if (!activePage?.brandingOverride) return;
    this.brandingService.setOverride(activePage.brandingOverride, true);
  }

  private getPrevAndNext$(
    video: VideoWithDocId,
    event: EventWithDocId
  ): VideoPageScope['prevAndNext$'] {
    return this.scopeService.scope$.pipe(
      switchMap((scope) => {
        const eventSource: VideoListSource = { eventId: event.docId };
        const sourceQuery: VideoSourceQueryOption = {
          scope: scope.scope,
          scopeId: scope.scopeId,
        };
        return combineLatest([
          getVideosFromSource(this.fs, eventSource, {
            ...sourceQuery,
            sort: ['DATE_TRIMMED', 'DESC'],
            _nowRef: video.startTime || video.uploadTime,
          }),
          getVideosFromSource(this.fs, eventSource, {
            ...sourceQuery,
            sort: ['DATE_TRIMMED', 'ASC'],
            _nowRef: video.endTime || video.uploadTime,
          }),
        ]);
      }),
      map(([prevVideos, nextVideos]) => {
        const prev = prevVideos
          .sort((v1, v2) => v2.uploadTime.getTime() - v1.uploadTime.getTime())
          .find((v) => v.docId !== video.docId && startsBefore(video, v));
        const next = nextVideos
          .sort((v1, v2) => v1.uploadTime.getTime() - v2.uploadTime.getTime())
          .find((v) => v.docId !== video.docId && startsAfter(video, v));
        return { prev, next };
      })
    );
  }

  private getCheapestPrice(
    video: VideoWithDocId,
    event?: EventWithDocId
  ): TicketPrice | undefined {
    if (!event) return undefined;

    const tickets = getTickets(event, {
      video,
      includeTicketsWithLimitedRedemptions: true,
    });

    return TicketsService.getCheapestAccess(tickets);
  }

  private getTagsModule(video: VideoWithDocId): TagsListModule {
    return { moduleType: 'TAGS_LIST', tags: video.tags };
  }

  private getLiveAndUpcoming(eventId: string): LiveAndUpcomingListModule {
    return {
      heading: $localize`:@@VideoPageLiveAndUpcomingVideosListTitle:Live and Upcoming Videos`,
      moduleType: 'LIVE_AND_UPCOMING_LIST',
      listSource: { eventId },
      layoutType: 'CAROUSEL',
      density: 'DENSE',
      size: 6,
    };
  }
}
